import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDownIcon } from 'lucide-react';

type StatusFilter = 'incomplete' | 'complete' | 'graded';

interface SessionStatusFilterDropdownProps {
    statusFilter: StatusFilter[];
    onStatusFilterChange: (statuses: StatusFilter[]) => void;
    disabled?: boolean;
}

const statusOptions = [
    { value: 'all' as const, label: 'All Sessions' },
    { value: 'incomplete' as const, label: 'Incomplete' },
    { value: 'complete' as const, label: 'Complete' },
    { value: 'graded' as const, label: 'Assessed' },
];

export function SessionStatusFilterDropdown({
    statusFilter,
    onStatusFilterChange,
    disabled = false
}: SessionStatusFilterDropdownProps) {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const getStatusFilterLabel = (statuses: StatusFilter[]) => {
        if (statuses.length === 0) return 'All Sessions';
        if (statuses.length === 1) {
            const option = statusOptions.find(opt => opt.value === statuses[0]);
            return option?.label || 'All Sessions';
        }
        return `${statuses.length} Selected`;
    };

    const handleStatusToggle = (status: StatusFilter | 'all') => {
        if (status === 'all') {
            // When "All Sessions" is clicked, clear all filters
            onStatusFilterChange([]);
        } else {
            const isSelected = statusFilter.includes(status);
            if (isSelected) {
                // Remove the status
                onStatusFilterChange(statusFilter.filter(s => s !== status));
            } else {
                // Add the status
                onStatusFilterChange([...statusFilter, status]);
            }
        }
        // Don't close dropdown - let user select multiple items
    };

    const isAllSelected = statusFilter.length === 0;
    const isStatusSelected = (status: StatusFilter) => statusFilter.includes(status);

    return (
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
            <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-48 justify-between" disabled={disabled}>
                    {getStatusFilterLabel(statusFilter)}
                    <ChevronDownIcon className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
                {statusOptions.map((option) => {
                    const isSelected = option.value === 'all' ? isAllSelected : isStatusSelected(option.value as StatusFilter);
                    return (
                        <DropdownMenuItem
                            key={option.value}
                            className="hover:cursor-pointer"
                            onClick={() => handleStatusToggle(option.value)}
                            onSelect={(e) => e.preventDefault()}
                        >
                            <div className="flex items-center gap-2 w-full">
                                <Checkbox
                                    checked={isSelected}
                                    className="pointer-events-none"
                                />
                                {option.label}
                            </div>
                        </DropdownMenuItem>
                    );
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
